/**
 * SIL-1444 - shared marketplace listing math/labels, used by BOTH the
 * listing card (`ListingCard.tsx`) and the filter card
 * (`MarketplaceFilters.tsx`), so the two surfaces cannot drift on what a
 * listing's price-per-unit, asset type(s), or location are.
 *
 * ## Price / Unit (spec Change 2)
 *
 * The server already sends a correct TOTAL price for every listing type
 * (`pricing.totalUsd` / `pricing.priceRange.total`): a single-node or
 * sku-quantity listing's `askingPriceUsd` is a per-unit ask that
 * `projectPricing` multiplies out, and a lot listing's `askingPriceUsd` is
 * already the lot's aggregate total (set at create time from the sum of
 * member FMV medians or the seller's override — `apps/api/src/routes/client/
 * listings.ts`'s lot branch). What the server does NOT correctly expose is
 * the PER-UNIT price for a lot: `pricing.pricePerNodeUsd` /
 * `priceRange.perNode` assume `nodes = listing.quantity`, which is NULL by
 * constraint for a lot listing (559_listing_lot.sql) and silently defaults
 * to 1 — so today's "Price / node" cell shows the lot's full total, not a
 * per-unit figure.
 *
 * Rather than re-deriving `nodes` on the API side (touching
 * `projectPricing`, shared by the seller-facing listings route too), this
 * computes Price / Unit CLIENT-side from two values that are already correct
 * and already sent: the total price, and `unitCountOf` (the real lot member
 * count via evidence, falling back to `quantity` exactly the way
 * `cardSubtitle` in ListingCard.tsx already does for "N nodes"). This is a
 * pure, three-listing-type-uniform formula — see `pricePerUnitUsd`'s doc
 * comment for why it holds for all three.
 *
 * ## Asset types (spec Changes 3 + 4)
 *
 * `listing.assetTypeIds` (server-computed, migration 975 +
 * `apps/api/src/routes/client/marketplace.ts`'s `assetTypesOf`) is already
 * the distinct, de-duplicated set — this module does not re-derive it, only
 * reads it (skew-tolerant: an older API that has not shipped the field omits
 * it, and `assetTypeIdsOf` returns `[]` rather than throwing) and supplies
 * the label + the Cloud VM filter-option exclusion both surfaces need.
 */
import type { MarketplaceListing } from '@siliconx/shared/dto/marketplace';
import { labelForAssetClass } from '@siliconx/shared/fmv/browse';
import { evidenceOf, nodeCountFromEvidence, pricingOf, type SkewProneListing } from '@siliconx/shared/ui/buyer-listing/listingShape';

type QuantityBearing = Pick<MarketplaceListing, 'quantity'>;

/**
 * The real unit count a price should be divided by: the lot's actual member
 * count when evidence carries one (>0 — true for 'asset' and 'lot'
 * listings, which both have backing `asset` rows via `listing_assets()`),
 * else the listing's own `quantity` column (true for 'sku' listings, which
 * have none), else 1. Mirrors `cardSubtitle`'s existing "N nodes" fallback
 * in ListingCard.tsx exactly, so the node count shown in the subtitle and
 * the unit count priced against can never disagree.
 */
export function unitCountOf(listing: SkewProneListing & QuantityBearing): number {
  const nodes = nodeCountFromEvidence(evidenceOf(listing));
  if (nodes > 0) return nodes;
  return listing.quantity !== null && listing.quantity !== undefined && listing.quantity > 0
    ? listing.quantity
    : 1;
}

/**
 * `totalUsd / unitCount`, clamped so a 0 (or negative, which should never
 * happen but must never divide-by-zero into `Infinity`) unit count cannot
 * produce a nonsense price. Holds uniformly across all three listing types:
 *
 *   - single node ('asset'): totalUsd is the one-asset ask, unitCount = 1 —
 *     Price / Unit == Total Price, as the spec requires.
 *   - sku quantity ('sku'): totalUsd = per-unit ask x quantity, unitCount =
 *     quantity — divides back out to the original per-unit ask.
 *   - lot ('lot'): totalUsd is the lot's aggregate ask, unitCount = the real
 *     lot member count — e.g. $849,242 / 40 = $21,231.05 (spec's own
 *     example).
 *
 * Rounded to whole dollars by the CALLER's formatter (the same
 * `Intl.NumberFormat` used for every other price on the card), not here —
 * this stays exact so a caller needing the unrounded value (a future
 * "x40 = $849,242" reconciliation display, say) is not fighting a
 * pre-rounded input.
 */
export { pricePerUnitUsd, pricePerUnitRange } from '@siliconx/shared/ui/buyer-listing/listingPriceView';

/**
 * The card's LOCATION cell (spec Change 1): state/region of the data
 * center, NEVER the facility name. A verified buyer's evidence carries
 * `siteState` (data_center.address->>'state', migration 975) — the DC's
 * actual state — and `site` (the facility name, e.g. "Ashburn DC1"), which
 * this function does not read at all, on purpose: falling through to it
 * would resurrect exactly the bug the spec calls out. `region` (the
 * business bucket, e.g. "US East") is the fallback for both tiers when no
 * state is on file, same as it already is on the unverified tier. `null`
 * means "say nothing" — the caller renders "Not stated".
 */
export function locationLabel(listing: SkewProneListing): string | null {
  const evidence = evidenceOf(listing);
  if (evidence === undefined) return null;
  if (evidence.tier === 'verified') return evidence.siteState ?? evidence.region ?? null;
  return evidence.region ?? null;
}

/** `listing.assetTypeIds`, tolerant of an API one version behind this
 *  field (SIL-1377 discipline) — an absent array renders no badges/options,
 *  never a crash. */
export function assetTypeIdsOf(listing: SkewProneListing & { assetTypeIds?: string[] }): string[] {
  return listing.assetTypeIds ?? [];
}

/** Human label for an asset_class_id slug, shared verbatim between the
 *  filter checkbox group and the card's badges (spec: "Human-readable
 *  labels, same as Change 3 filter labels"). */
export const assetTypeLabel = labelForAssetClass;

/**
 * Asset types the marketplace does not sell standalone — excluded from the
 * FILTER's offered checkbox options (spec Change 3), but never from
 * `assetTypeIdsOf`'s output or a card's badges: a lot that happens to carry
 * a Cloud VM member among other types still shows every type it has, and a
 * buyer simply cannot narrow the browse to Cloud VM specifically.
 */
const EXCLUDED_FILTER_ASSET_TYPES: ReadonlySet<string> = new Set(['cloud_vm']);

export function isFilterableAssetType(assetTypeId: string): boolean {
  return !EXCLUDED_FILTER_ASSET_TYPES.has(assetTypeId);
}

/** `pricingOf` re-exported so callers of this module need one import for
 *  the whole "price a listing" concern. */
export { pricingOf };
